import React, { useEffect } from 'react';
import { useMachine } from '@xstate/react';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { commonErrors as Errors } from '@zapier/toolbox-redux-framework';

import ErrorModal from 'app/developer-v3/components/PartnerSupportForm/components/ErrorModal';

import { useFormContext } from 'app/developer-v3/components/PartnerSupportForm/contexts/FormContext';
import {
  AVRO_EVENT_USER_INTERACTION,
  EVENT_CONTACT_ERROR,
  EVENT_CONTACT_SENT,
} from 'app/developer-v3/components/PartnerSupportForm/constants';

import formMachine from 'app/developer-v3/components/PartnerSupportForm/machine';

import State from './State';

const Form = () => {
  const { doc, from_url, initialDataSet, user } = useFormContext();
  const { trackEvent } = useTrackingContext();

  const [state, send, service] = useMachine(formMachine, {
    context: {
      data: initialDataSet,
      meta: {
        doc,
        email: user && user.email,
        from_url,
        full_name: user && user.fullName,
        // meta.email is setup to be user-editable if logged out, so this tracks if the user was really logged in:
        logged_in: user && !!user.email,
      },
    },
  });

  // once we have a service
  useEffect(() => {
    const subscription = service.subscribe(changedState => {
      const { context, event } = changedState;

      // TODO: Probably can move the trackPathEvent() calls in Questions and Submitted here

      if (event.type === 'done.invoke.submit') {
        trackEvent(EVENT_CONTACT_SENT, {
          topic: context.nodes.find(node => node.id === 'topic').data,
        });
        trackEvent(AVRO_EVENT_USER_INTERACTION, {
          system_name: 'partner_contact_form',
          interaction_name: 'contact_form_sent',
          option_selected: context.nodes.find(node => node.id === 'topic').data,
          interaction_raw_text: 'Send',
          interaction_goal: 'send support request',
          event_action: 'click',
        });
      } else if (event.type === 'error.platform.submit') {
        trackEvent(EVENT_CONTACT_ERROR, {
          error: Errors.DataPostError.message(event.data),
        });
      }
    });

    return subscription.unsubscribe;
  }, [service, trackEvent]);

  const unanswer = (pathId: string) => {
    send('UNANSWER', { pathId });
  };

  const answer = (pathId: string, data?: any) => {
    send('ANSWER', { pathId, data });
  };

  const confirm = () => {
    send('CONFIRM');
  };

  const unconfirm = () => {
    send('UNCONFIRM');
  };

  const submit = () => {
    send('SUBMIT');
  };

  const clearError = () => {
    send('CLEAR_ERROR');
  };

  const setMeta = (key: string, value: string) => {
    send('SET_META', { key, value });
  };

  const actions = {
    unanswer,
    confirm,
    unconfirm,
    answer,
    setMeta,
    submit,
  };

  return (
    <React.Fragment>
      <State
        actions={actions}
        nodes={state.context.nodes}
        state={state.value}
      />
      {state.context.error && (
        <ErrorModal
          errors={[state.context.error]}
          isFromContactForm={true}
          onClose={clearError}
        />
      )}
    </React.Fragment>
  );
};

export default Form;
